import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import Home from "./pages/Home";
import Chat from "./pages/Chat";
import CalendarPage from "./pages/CalendarPage";
import UploadPage from "./pages/UploadPage";
import ParentPage from "./pages/ParentPage";
import FocusPage from "./pages/FocusPage";
import StudyPage from "./pages/StudyPage";
import StudySetPage from "./pages/StudySetPage";
import AuthPage from "./pages/AuthPage";
import LandingPage from "./pages/LandingPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import OnboardingPage from "./pages/OnboardingPage";
import BillingPage from "./pages/BillingPage";
import PaymentResult from "./pages/PaymentResult";
import { BottomNav } from "./components/BottomNav";
import { AuthProvider, useAuth } from "./context/AuthContext";

const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
    <Loader2 size={36} className="animate-spin text-[#4D96FF]" />
  </div>
);

function Gate({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (user === false) return <Navigate to="/auth" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  if (!user.subscription?.access) return <Navigate to="/billing" replace />;
  return children;
}

function RequireUser({ children }) {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (user === false) return <Navigate to="/auth" replace />;
  return children;
}

function HomeOrLanding() {
  const { user } = useAuth();
  if (user === null) return <LoadingScreen />;
  if (user === false) return <LandingPage />;
  return <Gate><Home /></Gate>;
}

function Shell() {
  const { user } = useAuth();
  const location = useLocation();
  const hideNav = ["/auth", "/onboarding", "/payment/success", "/payment/cancel", "/forgot-password", "/reset-password"].includes(location.pathname)
    || !user || !user.onboarded;
  return (
    <>
      <Routes>
        <Route path="/auth" element={user === null ? <LoadingScreen /> : user ? <Navigate to="/" replace /> : <AuthPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/onboarding" element={<RequireUser><OnboardingPage /></RequireUser>} />
        <Route path="/billing" element={<RequireUser><BillingPage /></RequireUser>} />
        <Route path="/payment/success" element={<RequireUser><PaymentResult /></RequireUser>} />
        <Route path="/payment/cancel" element={<RequireUser><PaymentResult cancelled /></RequireUser>} />
        <Route path="/" element={<HomeOrLanding />} />
        <Route path="/chat" element={<Gate><Chat /></Gate>} />
        <Route path="/calendar" element={<Gate><CalendarPage /></Gate>} />
        <Route path="/upload" element={<Gate><UploadPage /></Gate>} />
        <Route path="/parent" element={<Gate><ParentPage /></Gate>} />
        <Route path="/focus" element={<Gate><FocusPage /></Gate>} />
        <Route path="/study" element={<Gate><StudyPage /></Gate>} />
        <Route path="/study/:id" element={<Gate><StudySetPage /></Gate>} />
      </Routes>
      {!hideNav && <BottomNav />}
    </>
  );
}

function App() {
  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A]">
      <div className="mascot-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL || ""}/mascot.png)` }} aria-hidden="true" />
      <div className="relative z-10">
        <AuthProvider>
          <BrowserRouter>
            <Shell />
          </BrowserRouter>
        </AuthProvider>
      </div>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
